/**
 * Turn an axios/DRF error into a message worth showing. DRF returns either
 * {detail: "..."} or {field: ["msg", ...]} (or {field: "msg"}).
 */
export function errorMessage(error: unknown, fallback: string): string {
  const err = error as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  // A string body is a message, unless it is an HTML error page (a 404 or 500
  // from the server itself rather than the API).
  if (typeof data === 'string') return /^\s*</.test(data) ? fallback : data;
  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (typeof obj.detail === 'string') return obj.detail;
    const entries = Object.entries(obj);
    if (entries.length) {
      const [field, value] = entries[0];
      const msg = Array.isArray(value) ? value[0] : value;
      if (typeof msg === 'string') {
        return field === 'non_field_errors' ? msg : `${field}: ${msg}`;
      }
    }
  }
  return fallback;
}

function collectMessages(value: unknown, out: string[]): void {
  if (typeof value === 'string') {
    if (value && !out.includes(value)) out.push(value);
  } else if (Array.isArray(value)) {
    value.forEach((item) => collectMessages(item, out));
  } else if (value && typeof value === 'object') {
    // A list field reports per item: {0: ["..."]}.
    Object.values(value as Record<string, unknown>).forEach((item) => collectMessages(item, out));
  }
}

/**
 * The per-field messages of a DRF validation error (400 {field: ["msg"]}), as
 * {field: "msg"}. Empty when the error is not about fields (a network error,
 * a 403 {detail}, an HTML error page), so the caller can fall back to
 * `errorMessage`.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data;
  if (response?.status !== 400 || !data || typeof data !== 'object' || Array.isArray(data)) return {};
  const out: Record<string, string> = {};
  Object.entries(data as Record<string, unknown>).forEach(([field, value]) => {
    if (field === 'detail') return;
    const messages: string[] = [];
    collectMessages(value, messages);
    if (messages.length) out[field] = messages.join(' ');
  });
  return out;
}
